/* ═══════════════════════════════════════════════════════════════
   The Designer — a tool opened from the MENU rather than the dock.

   Josh's law (docs/DESIGNER_UNIFIED_PLAN.md §1): the menu is the
   permanent, complete access path to every tool; docking is a
   convenience layered on top. So every palette must be fully operable
   without ever being docked — this is the surface that guarantees it.

   Portalled to <body>, position: fixed, viewport-clamped, top of the
   z-scale (DESIGN §4 "context UI always on top"). Drag it by its title
   bar, resize it from the corner, dock it with one click.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { subscribeUiScale } from '../lib/uiScale';
import { REM_BASE_PX } from '../lib/rem';
import { readPref, writePref } from '../lib/usePref';
import { appLayerProps } from '../components/ui/app-layer';

export interface ToolPanelProps {
  title: string;
  /** Where the panel first appears (the toolbar button that opened it). */
  anchor?: { left: number; bottom: number } | null;
  /** Exact spot to open at — where a drag-to-float released (§2.8). Wins
      over `anchor` and skips the cascade: the panel appears under the
      pointer, which is where the user just said they wanted it. */
  at?: { left: number; top: number } | null;
  /** Stacking offset so two panels opened together do not sit on top of
      each other. */
  index?: number;
  onDock: () => void;
  onClose: () => void;
  children: React.ReactNode;
}

/* ─── Geometry (DESIGN.md §3.6) ───
   A floating tool panel is sized as a FRACTION of the window it floats in,
   never a fixed pixel box, with a floor and a cap in rem so both follow the
   interface dial. The fractions below ARE the shipped default size, expressed
   against the 1500×940 reference workspace this panel was drawn for:
     width  360 / 1500 = 0.24
     height 460 /  940 ≈ 0.4894
   so it still opens at exactly 360×460 there, and grows with a bigger window
   instead of stranding a postage stamp in the corner. */
const DEFAULT_W_FRACTION = 360 / 1500;
const DEFAULT_H_FRACTION = 460 / 940;
const MIN_W_REM = 17; // 272px at 100% — narrower than this and controls wrap
const MAX_W_REM = 34; // 544px at 100%
const MIN_H_REM = 15; // 240px at 100%
const MAX_H_REM = 44; // 704px at 100%
const EDGE_REM = 0.5; // breathing room against the viewport edge
const BOTTOM_REM = 7.5; // keep the title bar reachable when dragged low
const CASCADE_REM = 1.75; // 28px at 100%

/** The size the user last dragged this panel to, kept per device. Floating
 *  panels used to lose their size the moment they closed. */
const SIZE_KEY = 'sf-toolpanel-size';

interface SizeFractions {
  wf: number;
  hf: number;
}

const rootPx = (): number => {
  try {
    return parseFloat(getComputedStyle(document.documentElement).fontSize) || REM_BASE_PX;
  } catch {
    return REM_BASE_PX;
  }
};
const remPx = (r: number): number => r * rootPx();

const DEFAULT_FRACTIONS: SizeFractions = { wf: DEFAULT_W_FRACTION, hf: DEFAULT_H_FRACTION };

/** D1 cross-surface parity: cloud `user_prefs` + localStorage fast cache
 *  (this used to be per-device only). */
function readFractions(): SizeFractions {
  const f = readPref<Partial<SizeFractions>>(SIZE_KEY, SIZE_KEY, DEFAULT_FRACTIONS);
  const wf = Number(f?.wf);
  const hf = Number(f?.hf);
  if (Number.isFinite(wf) && Number.isFinite(hf) && wf > 0 && hf > 0) return { wf, hf };
  return DEFAULT_FRACTIONS;
}

function writeFractions(f: SizeFractions): void {
  writePref(SIZE_KEY, SIZE_KEY, f);
}

const between = (v: number, lo: number, hi: number) => Math.round(Math.max(lo, Math.min(v, hi)));

/** fraction of the window → px, floored and capped in rem. */
function sizeOf(f: SizeFractions): { w: number; h: number } {
  const edge = remPx(EDGE_REM);
  return {
    w: between(
      f.wf * window.innerWidth,
      remPx(MIN_W_REM),
      Math.min(remPx(MAX_W_REM), Math.max(remPx(MIN_W_REM), window.innerWidth - edge * 2))
    ),
    h: between(
      f.hf * window.innerHeight,
      remPx(MIN_H_REM),
      Math.min(remPx(MAX_H_REM), Math.max(remPx(MIN_H_REM), window.innerHeight - edge * 2))
    ),
  };
}

/** Never strand a panel off-screen — the clamp the drag and every resize use. */
function clampPos(left: number, top: number, w: number): { left: number; top: number } {
  const edge = remPx(EDGE_REM);
  const maxL = Math.max(edge, window.innerWidth - w - edge);
  const maxT = Math.max(edge, window.innerHeight - remPx(BOTTOM_REM));
  return { left: Math.max(edge, Math.min(left, maxL)), top: Math.max(edge, Math.min(top, maxT)) };
}

export function ToolPanel({
  title,
  anchor,
  at,
  index = 0,
  onDock,
  onClose,
  children,
}: ToolPanelProps) {
  // Size is a fraction of the window (see the constants above): the default on
  // first open, whatever the user last resized it to after that.
  const [size, setSize] = React.useState(() => sizeOf(readFractions()));
  const secRef = React.useRef<HTMLElement>(null);
  // The last size WE applied (default or a window/dial recompute). The corner
  // grip's ResizeObserver below must only store a fraction the USER dragged —
  // storing a size the floor/cap produced would let the stored fraction drift
  // every time the clamp bit (a small window would permanently widen it).
  const derivedSizeRef = React.useRef<{ w: number; h: number } | null>(null);
  if (derivedSizeRef.current === null) derivedSizeRef.current = size;

  const clamp = React.useCallback(
    (left: number, top: number) => clampPos(left, top, size.w),
    [size.w]
  );

  // Cascade DOWN-AND-LEFT from the right edge: each panel steps clear of the
  // one before it instead of stacking on the same clamped x and hiding it.
  const [pos, setPos] = React.useState(() => {
    const { w } = sizeOf(readFractions());
    const cascade = remPx(CASCADE_REM);
    const edge = remPx(EDGE_REM);
    return at
      ? // Dropped here on purpose: open under the pointer, no cascade.
        clampPos(at.left - w / 2, at.top - remPx(0.875), w)
      : clampPos(
          (anchor?.left ?? window.innerWidth - w - remPx(1.5)) - index * cascade,
          (anchor?.bottom ?? remPx(6)) + edge + index * cascade,
          w
        );
  });

  // Esc closes — the same exit every overlay on this surface offers.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // The window (or the interface dial) changing re-applies the stored
  // fraction — the panel keeps its share of the workspace — and a shrinking
  // window must never strand it off-screen.
  React.useEffect(() => {
    const onResize = () => {
      const next = sizeOf(readFractions());
      derivedSizeRef.current = next;
      setSize(next);
      setPos((p) => clampPos(p.left, p.top, next.w));
    };
    window.addEventListener('resize', onResize);
    const unsubscribe = subscribeUiScale(onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      unsubscribe();
    };
  }, []);

  // The corner grip is CSS `resize`, which writes straight to the element —
  // observe it so the new size is stored (as a fraction) and survives a close
  // and the next open, per device.
  React.useEffect(() => {
    const el = secRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const ro = new ResizeObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height || !window.innerWidth || !window.innerHeight) return;
        const derived = derivedSizeRef.current;
        if (derived && Math.abs(derived.w - r.width) <= 1 && Math.abs(derived.h - r.height) <= 1)
          return; // our own size, not a drag — never re-store a clamped value
        writeFractions({ wf: r.width / window.innerWidth, hf: r.height / window.innerHeight });
        derivedSizeRef.current = { w: Math.round(r.width), h: Math.round(r.height) };
        setSize({ w: Math.round(r.width), h: Math.round(r.height) });
      }, 250);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dragRef = React.useRef<{ dx: number; dy: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    // Only the title bar's empty space drags — never its buttons.
    if ((e.target as HTMLElement).closest('button')) return;
    dragRef.current = { dx: e.clientX - pos.left, dy: e.clientY - pos.top };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    setPos(clamp(e.clientX - d.dx, e.clientY - d.dy));
  };
  const endDrag = (e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already gone */
    }
  };

  return createPortal(
    <section
      {...appLayerProps}
      ref={secRef}
      className="dsg-toolpanel"
      role="dialog"
      aria-label={title}
      style={{ left: pos.left, top: pos.top, width: size.w, height: size.h }}
    >
      <header
        className="dsg-toolpanel-bar"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <span className="dsg-toolpanel-title">{title}</span>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onDock}
          aria-label={`Dock ${title} into the workspace`}
          title="Dock into the workspace"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d="M15 4v16" />
          </svg>
        </button>
        <button
          type="button"
          className="dsg-icon-btn"
          onClick={onClose}
          aria-label={`Close ${title}`}
          title="Close"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>
      <div className="dsg-toolpanel-body">{children}</div>
    </section>,
    document.body
  );
}
